import { useNode } from "@craftjs/core";
import type { TFunction } from "i18next";
import * as React from "react";
import { useTranslation } from "react-i18next";

import SettingsTitleWrapper from "@/components/editor/shared/settings-title-wrapper";
import type { StandaloneNavigationProps } from "@/components/editor/themes/crosby/blocks/headers/standalone-navigation/standalone-navigation-component";
import { Warning } from "@/components/icons/warning";
import { SwitchInput } from "@/components/shared/forms";
import useViewportContext from "@/hooks/use-viewport-context";

export function StandaloneNavigationSettings() {
  const { viewport } = useViewportContext();
  const { t } = useTranslation();
  const {
    actions: { setProp },
    props,
  } = useNode((node) => ({
    props: node.data.props,
  }));

  const createToggleHandler = (
    prop: keyof Pick<StandaloneNavigationProps, "isSticky" | "showCta">
  ) => () => {
    setProp((props) => {
      return (props[prop] = props[prop] === undefined ? true : !props[prop]);
    });
  };

  return (
    <SettingsTitleWrapper title={t("preference-panel.standalone-navigation")}>
      <div className="flex flex-col gap-2">
        <SwitchInput
          label={t("preference-panel.sticky-navigation")}
          onChange={createToggleHandler("isSticky")}
          checked={!!props.isSticky}
          data-testid="toggle-sticky-navigation"
        />

        <SwitchInput
          label={t("preference-panel.show-call-to-action")}
          onChange={createToggleHandler("showCta")}
          checked={!!props.showCta}
          data-testid="toggle-cta-navigation"
        />
      </div>

      {viewport === "mobile" && <WarningBox t={t} />}
    </SettingsTitleWrapper>
  );
}

const WarningBox = ({ t }: { t: TFunction }) => (
  <div className="mt-6 rounded-md bg-gray-100 px-4 py-3">
    <Warning className="mb-2.5 size-5 text-yellow-400" />
    <span className="text-sm text-gray-900">
      {t("preference-panel.editing-is-disabled")}
    </span>
    <span className="text-sm font-semibold text-gray-600">
      {t("preference-panel.navigation-links-are-not-editable")}
    </span>
  </div>
);
